/**
 * 轻量 i18n — 模块级 t()，中文原文为 key，回退链 lang → en → 原文
 * 切换语言即写 localStorage 并刷新页面（模块级常量也能正确刷新）
 */
import React from 'react';
import { AUTO_DICT } from './dict-auto';

export type Lang = 'zh' | 'en' | 'ja' | 'de' | 'fr' | 'es';

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
];

/** 通用词条（高频 UI 词，人工校对 6 语言）；AUTO_DICT 为机器翻译批量词条 */
const COMMON: Record<string, Partial<Record<Lang, string>>> = {
  '设置': { en: 'Settings', ja: '設定', de: 'Einstellungen', fr: 'Paramètres', es: 'Ajustes' },
  '套餐与订阅': { en: 'Plan & Billing', ja: 'プランと請求', de: 'Tarif & Abrechnung', fr: 'Forfait & Facturation', es: 'Plan y Facturación' },
  '保存': { en: 'Save', ja: '保存', de: 'Speichern', fr: 'Enregistrer', es: 'Guardar' },
  '已保存': { en: 'Saved', ja: '保存しました', de: 'Gespeichert', fr: 'Enregistré', es: 'Guardado' },
  '保存中...': { en: 'Saving...', ja: '保存中...', de: 'Speichern...', fr: 'Enregistrement...', es: 'Guardando...' },
  '加载中...': { en: 'Loading...', ja: '読み込み中...', de: 'Laden...', fr: 'Chargement...', es: 'Cargando...' },
  '暂无数据': { en: 'No data yet', ja: 'データなし', de: 'Keine Daten', fr: 'Aucune donnée', es: 'Sin datos' },
  '操作': { en: 'Actions', ja: '操作', de: 'Aktionen', fr: 'Actions', es: 'Acciones' },
  '状态': { en: 'Status', ja: 'ステータス', de: 'Status', fr: 'Statut', es: 'Estado' },
  '时间': { en: 'Time', ja: '日時', de: 'Zeit', fr: 'Date', es: 'Fecha' },
  '客户': { en: 'Customer', ja: '顧客', de: 'Kunde', fr: 'Client', es: 'Cliente' },
  '商品': { en: 'Product', ja: '商品', de: 'Produkt', fr: 'Produit', es: 'Producto' },
  '金额': { en: 'Amount', ja: '金額', de: 'Betrag', fr: 'Montant', es: 'Importe' },
  '删除': { en: 'Delete', ja: '削除', de: 'Löschen', fr: 'Supprimer', es: 'Eliminar' },
  '添加': { en: 'Add', ja: '追加', de: 'Hinzufügen', fr: 'Ajouter', es: 'Añadir' },
  '批准': { en: 'Approve', ja: '承認', de: 'Genehmigen', fr: 'Approuver', es: 'Aprobar' },
  '拒绝': { en: 'Reject', ja: '拒否', de: 'Ablehnen', fr: 'Rejeter', es: 'Rechazar' },
  '执行': { en: 'Apply', ja: '実行', de: 'Ausführen', fr: 'Exécuter', es: 'Ejecutar' },
  '全部': { en: 'All', ja: 'すべて', de: 'Alle', fr: 'Tout', es: 'Todo' },
  '订单': { en: 'Order', ja: '注文', de: 'Bestellung', fr: 'Commande', es: 'Pedido' },
  '原因': { en: 'Reason', ja: '理由', de: 'Grund', fr: 'Raison', es: 'Motivo' },
  '关闭': { en: 'Close', ja: '閉じる', de: 'Schließen', fr: 'Fermer', es: 'Cerrar' },
  '移除': { en: 'Remove', ja: '削除', de: 'Entfernen', fr: 'Retirer', es: 'Quitar' },
  '当前套餐': { en: 'Current plan', ja: '現在のプラン', de: 'Aktueller Tarif', fr: 'Forfait actuel', es: 'Plan actual' },
  '订阅': { en: 'Subscribe', ja: '購読する', de: 'Abonnieren', fr: "S'abonner", es: 'Suscribirse' },
  '月': { en: 'mo', ja: '月', de: 'Mon.', fr: 'mois', es: 'mes' },
  '免费试用 7 天，随时取消': { en: '7-day free trial, cancel anytime', ja: '7日間無料、いつでもキャンセル可', de: '7 Tage kostenlos testen', fr: 'Essai gratuit de 7 jours', es: 'Prueba gratis de 7 días' },
  '自带 AI Key 享 7 折': { en: '30% off with your own AI key', ja: '自前のAIキーで30%オフ', de: '30% Rabatt mit eigenem AI-Key', fr: '-30% avec votre clé AI', es: '30% dto. con tu clave AI' },
  'AI 服务配置': { en: 'AI Provider', ja: 'AIプロバイダー', de: 'AI-Anbieter', fr: 'Fournisseur AI', es: 'Proveedor de IA' },
  '使用平台内置 AI（默认）': { en: 'Use platform AI (default)', ja: 'プラットフォームAIを使用（デフォルト）', de: 'Plattform-AI verwenden (Standard)', fr: 'AI de la plateforme (défaut)', es: 'IA de la plataforma (predeterminado)' },
  '自有 AI 作为备用（平台失败时启用）': { en: 'My AI as fallback (when platform fails)', ja: '自前AIをフォールバックに', de: 'Eigene AI als Fallback', fr: 'Mon AI en secours', es: 'Mi IA como respaldo' },
  '优先使用自有 AI': { en: 'Prefer my AI', ja: '自前AIを優先', de: 'Eigene AI bevorzugen', fr: 'Préférer mon AI', es: 'Preferir mi IA' },
  '接口地址（OpenAI 兼容）': { en: 'Base URL (OpenAI-compatible)', ja: 'ベースURL（OpenAI互換）', de: 'Basis-URL (OpenAI-kompatibel)', fr: 'URL de base (compatible OpenAI)', es: 'URL base (compatible OpenAI)' },
  '模型': { en: 'Model', ja: 'モデル', de: 'Modell', fr: 'Modèle', es: 'Modelo' },
  '测试连接': { en: 'Test connection', ja: '接続テスト', de: 'Verbindung testen', fr: 'Tester la connexion', es: 'Probar conexión' },
  '连接成功': { en: 'Connection OK', ja: '接続成功', de: 'Verbindung OK', fr: 'Connexion OK', es: 'Conexión OK' },
  '连接失败': { en: 'Connection failed', ja: '接続失敗', de: 'Verbindung fehlgeschlagen', fr: 'Échec de connexion', es: 'Error de conexión' },
};

const STORAGE_KEY = 'omnifuse_lang';

function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (saved && LANGS.some(l => l.code === saved)) return saved;
    const nav = (navigator.language || 'en').toLowerCase();
    if (nav.startsWith('zh')) return 'zh';
    if (nav.startsWith('ja')) return 'ja';
    if (nav.startsWith('de')) return 'de';
    if (nav.startsWith('fr')) return 'fr';
    if (nav.startsWith('es')) return 'es';
    return 'en';
  } catch { return 'zh'; }
}

export const currentLang: Lang = detectLang();

/** 翻译函数 — 中文原文即 key */
export function t(key: string): string {
  if (currentLang === 'zh') return key;
  const entry = COMMON[key] || (AUTO_DICT as any)[key];
  return entry?.[currentLang] || entry?.en || key;
}

export function setLang(l: Lang) {
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  window.location.reload();
}

export function LanguageSwitcher() {
  return (
    <select
      value={currentLang}
      onChange={e => setLang(e.target.value as Lang)}
      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, background: '#fff', cursor: 'pointer' }}
      aria-label="Language"
    >
      {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
    </select>
  );
}
